import { useMemo } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useBookmarks } from "@/hooks/useSocial"
import { usePostFilters } from "@/hooks/usePostFilters"
import { useFilteredPosts } from "@/hooks/useFilteredPosts"
import { useEventsByIds } from "@/hooks/useProfiles"
import { useAuth } from "@/state/AuthContext"
import { useSettings } from "@/state/SettingsContext"
import { NoteCard } from "@/components/NoteCard"
import { PostTypeFilter } from "@/components/PostTypeFilter"
import { PostFilters } from "@/components/filters/PostFilters"
import { EmptyState, Skeleton } from "@/components/Feedback"
import { Icon } from "@/components/Icon"
import { postTypeSelection } from "@/nostr/api/filters/filterEngine"
import type { NostrEvent } from "@/nostr/types"

/** Bookmarked notes (kind 10003), resolved against the connected relays. */
export function BookmarksPage() {
  const { t } = useTranslation()
  const { pubkey, canSign } = useAuth()
  const { settings } = useSettings()
  const bookmarks = useBookmarks()
  const events = useEventsByIds(bookmarks.noteIds)
  const postFilters = usePostFilters("bookmarks")
  const filters = postFilters.filters

  const savedNotes = useMemo(
    () =>
      bookmarks.noteIds
        .map((id) => events.get(id))
        .filter((event): event is NostrEvent => Boolean(event)),
    [bookmarks.noteIds, events],
  )
  const visible = useFilteredPosts(savedNotes, filters, { mutedWords: settings.mutedWords })
  const notes = visible.events
  const missing = bookmarks.noteIds.length - savedNotes.length

  if (!pubkey) {
    return (
      <div className="page">
        <EmptyState
          icon="bookmark"
          title={t("Log in to use bookmarks")}
          description={t("Bookmarks are stored in your own kind 10003 event, not on a server.")}
          action={
            <Link to="/settings" className="btn btn-primary">
              {t("Settings")}
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="page bookmarks-page">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{t("Bookmarks")}</h1>
          <p className="page-subtitle">
            {bookmarks.noteIds.length} saved · {canSign ? t("signed in") : t("read-only")}
          </p>
        </div>
      </header>

      {bookmarks.loading && notes.length === 0 && <Skeleton lines={3} />}

      {!bookmarks.loading && bookmarks.noteIds.length === 0 && (
        <EmptyState
          icon="bookmark"
          title={t("No bookmarks yet")}
          description={t("Open a note and press the bookmark icon to save it here.")}
          action={
            <Link to="/" className="btn btn-primary">
              <Icon name="home" size={16} /> {t("Back home")}
            </Link>
          }
        />
      )}

      {savedNotes.length > 0 && (
        <div className="filter-bar">
          <PostTypeFilter
            value={postTypeSelection(filters)}
            onChange={(selection) =>
              postFilters.patch({ postTypes: selection === "all" ? [] : [selection] })
            }
          />
          <PostFilters
            value={filters}
            active={postFilters.active}
            onApply={(next) => postFilters.patch(next)}
            onClearAll={postFilters.clearAll}
            onRemove={postFilters.remove}
            categories={["content", "time", "people"]}
            showFollowing={false}
            mutedWordCount={settings.mutedWords.length}
          />
        </div>
      )}

      {!bookmarks.loading && savedNotes.length > 0 && notes.length === 0 && (
        <EmptyState
          icon="bookmark"
          title={t("No bookmarks match the filters")}
          description={t("Saved posts that do not match the active filters are hidden.")}
          action={
            <button type="button" className="btn btn-ghost" onClick={postFilters.clearAll}>
              {t("Clear all")}
            </button>
          }
        />
      )}

      {notes.map((note) => (
        <NoteCard key={note.id} event={note} showBookmark />
      ))}

      {visible.hidden > 0 && notes.length > 0 && (
        <p className="muted small">
          {t("{n} saved posts are hidden by the active filters", { n: visible.hidden })}
        </p>
      )}

      {missing > 0 && (
        <p className="muted small">
          {missing} bookmark{missing === 1 ? "" : "s"} could not be loaded from the connected relays.
        </p>
      )}
    </div>
  )
}
